/**
 * FlowNotes API Client - Flows
 * API calls for flows engine v1 - flow definitions and run history
 */

import { fetchWithAuth } from './core';
import { handleBasecamp401 } from '@/lib/basecamp-api';
import { withWorkspaceScope } from '@/lib/workspace-scope-query';

export interface FlowRun {
  id: string;
  flow_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'policy_blocked';
  trigger_type: string;
  trigger_payload: Record<string, unknown> | null;
  started_at: string;
  completed_at: string | null;
  error: string | null;
  cost_usd: number | null;
  output_thread_id: string | null;
}

export interface FlowTrigger {
  id: string;
  config: Record<string, unknown>;
}

export interface FlowRunEvent {
  id: string;
  run_id: string;
  flow_id: string;
  event_type: string;
  step_id: string | null;
  payload: Record<string, unknown>;
  emitted_at: string;
}

export interface Flow {
  id: string;
  title: string;
  slug: string;
  file_path: string;
  enabled: boolean;
  /** Flow-level Shadow. Missing on older responses means false (Live when enabled). */
  shadow?: boolean;
  paused: boolean;
  paused_reason: string | null;
  last_run_at: string | null;
  last_run_status: string | null;
  triggers: FlowTrigger[];
  tags: string[];
  /** Display tier badge in sidebar inventory (optional) */
  tier?: string;
  /** Health signal for incident queue when not `ok` */
  health?: string;
}

/** Fetch flows. Pass active workspace ids so other workspaces are not included. */
export async function getFlows(workspaceIds?: string[]): Promise<Flow[]> {
  const response = await fetchWithAuth(withWorkspaceScope('/api/flows', workspaceIds ?? []));
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = (await response.json()) as { error?: string };
      if (body?.error) detail = body.error;
    } catch {
      /* non-JSON body */
    }
    throw new Error(`Failed to fetch flows (${response.status}): ${detail}`);
  }
  const data = await response.json();
  return data.flows ?? data;
}

/** Fetch run history for a specific flow */
export async function getFlowRuns(flowId: string): Promise<FlowRun[]> {
  const response = await fetchWithAuth(`/api/flows/${flowId}/runs`);
  if (!response.ok) {
    throw new Error(`Failed to fetch flow runs: ${response.statusText}`);
  }
  const data = await response.json();
  return data.runs ?? data;
}

export async function getFlowRunEvents(flowId: string, runId: string): Promise<FlowRunEvent[]> {
  const response = await fetchWithAuth(`/api/flows/${flowId}/runs/${runId}/events`);
  if (!response.ok) {
    throw new Error(`Failed to fetch run events: ${response.statusText}`);
  }
  const data = await response.json();
  const events = data.events ?? data;
  return Array.isArray(events) ? events : [];
}

export type FlowPosture = 'disabled' | 'shadow' | 'live';

export interface FlowActivityRun {
  id: string;
  flow_id: string;
  status: string;
  trigger_type: string;
  started_at: string;
  completed_at: string | null;
  error: string | null;
  cost_usd: number | null;
  title: string;
  file_path: string;
  duration_ms: number | null;
  conversation_id: string | null;
  step_ids: string[];
}

export interface FlowActivityCounts {
  successful_24h: number;
  failed_24h: number;
  exceptions_7d: number;
  cost_7d: number;
}

export interface FlowActivityQuery {
  scope?: 'mine' | 'team';
  status?: string;
  triggerType?: string;
  flowId?: string;
  sinceHours?: number;
  q?: string;
}

export interface FlowActivityResult {
  runs: FlowActivityRun[];
  counts: FlowActivityCounts;
}

export interface FlowCatalogStep {
  id: string;
  dangerous: boolean;
  required: string[];
}

export function flowPosture(flow: { enabled: boolean; shadow?: boolean }): FlowPosture {
  if (!flow.enabled) return 'disabled';
  if (flow.shadow) return 'shadow';
  return 'live';
}

/** Set Disabled / Shadow / Live. Disabled does not clear a stored shadow flag. */
export async function setFlowPosture(
  flowId: string,
  mode: FlowPosture,
): Promise<{ success: boolean; mode: FlowPosture }> {
  const response = await fetchWithAuth(`/api/flows/${flowId}/posture`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode }),
  });
  if (!response.ok) {
    throw new Error(`Failed to set flow posture: ${response.statusText}`);
  }
  return response.json();
}

const EMPTY_ACTIVITY_COUNTS: FlowActivityCounts = {
  successful_24h: 0,
  failed_24h: 0,
  exceptions_7d: 0,
  cost_7d: 0,
};

export async function getFlowActivity(
  workspaceIds?: string[],
  query: FlowActivityQuery = {},
): Promise<FlowActivityResult> {
  const params = new URLSearchParams();
  if (query.scope === 'mine') params.set('scope', 'mine');
  if (query.status) params.set('status', query.status);
  if (query.triggerType) params.set('trigger_type', query.triggerType);
  if (query.flowId) params.set('flow_id', query.flowId);
  if (query.sinceHours) params.set('since_hours', String(query.sinceHours));
  if (query.q) params.set('q', query.q);
  const qs = params.toString();
  const path = qs ? `/api/flows/activity?${qs}` : '/api/flows/activity';
  const response = await fetchWithAuth(withWorkspaceScope(path, workspaceIds ?? []));
  if (!response.ok) {
    throw new Error(`Failed to fetch flow activity: ${response.statusText}`);
  }
  const data = await response.json();
  return {
    runs: Array.isArray(data.runs) ? data.runs : [],
    counts: data.counts ?? EMPTY_ACTIVITY_COUNTS,
  };
}

export async function getFlowCatalog(): Promise<FlowCatalogStep[]> {
  const response = await fetchWithAuth('/api/flows/catalog');
  if (!response.ok) {
    throw new Error(`Failed to fetch flow catalog: ${response.statusText}`);
  }
  const data = await response.json();
  return data.steps ?? [];
}

/** Enable a flow */
export async function enableFlow(flowId: string): Promise<{ success: boolean }> {
  const response = await fetchWithAuth(`/api/flows/${flowId}/enable`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Failed to enable flow: ${response.statusText}`);
  }
  return response.json();
}

/** Disable a flow */
export async function disableFlow(flowId: string): Promise<{ success: boolean }> {
  const response = await fetchWithAuth(`/api/flows/${flowId}/disable`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Failed to disable flow: ${response.statusText}`);
  }
  return response.json();
}

/** Manually trigger a flow run */
export async function triggerFlowRun(
  flowId: string,
  payload: Record<string, unknown> = {},
): Promise<{ runId: string }> {
  const response = await fetchWithAuth(`/api/flows/${flowId}/trigger`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ payload }),
  });
  if (response.status === 401) {
    const data = await response.json().catch(() => ({}));
    handleBasecamp401(response, data);
  }
  if (response.status === 409) {
    const data = await response.json().catch(() => ({}));
    throw Object.assign(new Error(data.error || 'A run is already in progress'), {
      status: 409,
      runId: data.runId,
    });
  }
  if (!response.ok) {
    throw new Error(`Failed to trigger flow: ${response.statusText}`);
  }
  const data = await response.json();
  const runId = data.run?.runId ?? data.runId;
  if (typeof runId !== 'string' || !runId) {
    throw new Error('API did not return run.runId');
  }
  return { runId };
}
